(() => {
  if (window.__mioClarityEventsInitialized) return;
  window.__mioClarityEventsInitialized = true;

  const body = document.body;
  const isEnabled = body?.dataset.mioAnalyticsEnabled === 'true' || window.__mioClarityTestMode === true;
  if (!isEnabled) return;

  const pendingCalls = [];
  const trackedPageEvents = new Set();
  let retryTimer = null;
  let retryCount = 0;
  let lastAddToCartAt = 0;
  let lastCheckoutAt = 0;

  const flush = () => {
    if (typeof window.clarity !== 'function') return false;

    while (pendingCalls.length) {
      window.clarity(...pendingCalls.shift());
    }

    if (retryTimer) {
      window.clearInterval(retryTimer);
      retryTimer = null;
    }
    return true;
  };

  const queue = (...args) => {
    if (typeof window.clarity === 'function') {
      window.clarity(...args);
      return;
    }

    pendingCalls.push(args);
    if (retryTimer) return;

    retryTimer = window.setInterval(() => {
      retryCount += 1;
      if (flush() || retryCount >= 60) {
        window.clearInterval(retryTimer);
        retryTimer = null;
        if (retryCount >= 60) pendingCalls.length = 0;
      }
    }, 500);
  };

  const setTag = (key, value) => {
    if (!value) return;
    queue('set', key, String(value).slice(0, 255));
  };

  const trackEvent = (name) => {
    if (window.__mioClarityTestMode === true) {
      window.__mioClarityTestEvents ||= [];
      window.__mioClarityTestEvents.push(name);
    }
    queue('event', name);
  };

  const trackPageEventOnce = (name) => {
    if (trackedPageEvents.has(name)) return;
    trackedPageEvents.add(name);
    trackEvent(name);
  };

  const recommendationStorageKey = 'mio_cart_recommendation_origins';
  const recommendationOrigins = () => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(recommendationStorageKey) || '[]');
      return Array.isArray(saved) ? saved.filter((item) => item && Date.now() - item.at < 30 * 60 * 1000).slice(-20) : [];
    } catch {
      return [];
    }
  };

  const trackCartRecommendation = (action, recommendation) => {
    if (!['viewed', 'clicked', 'added'].includes(action) || !recommendation?.handle) return;
    const kind = recommendation.kind === 'mill' ? 'mill' : 'cross_category';
    setTag('mio_recommendation_surface', recommendation.surface || 'cart-drawer');
    setTag('mio_recommendation_product', recommendation.handle);
    setTag('mio_recommendation_category', recommendation.category);
    trackEvent(`mio_cart_recommendation_${kind}_${action}`);
    if (['tokyo_duo', 'stella_amber', 'stella_clear'].includes(recommendation.offerId)) {
      trackEvent(`mio_pair_offer_${recommendation.offerId}_${action}`);
    }
  };

  const trackAddToCart = (source = 'unknown', millMode, productHandle) => {
    const now = Date.now();
    if (now - lastAddToCartAt < 1000) return;
    lastAddToCartAt = now;
    const origin = source === 'product-form'
      ? recommendationOrigins().find((item) => item.handle === productHandle)
      : null;
    if (origin) {
      source = 'cart-upsell-product';
      trackCartRecommendation('added', origin);
    }
    setTag('mio_cart_source', source);
    trackEvent('mio_add_to_cart');
    if (millMode === 'pair' || millMode === 'unit') trackEvent(`mio_mill_${millMode}_added`);
  };

  const trackMillAddError = (mode, reason) => {
    if (!['pair', 'unit'].includes(mode) || !['selection', 'rejected', 'request'].includes(reason)) return;
    setTag('mio_mill_error_kind', reason);
    trackEvent(`mio_mill_${mode}_error`);
  };

  const trackBeginCheckout = (source = 'unknown') => {
    const now = Date.now();
    if (now - lastCheckoutAt < 1000) return;
    lastCheckoutAt = now;
    setTag('mio_checkout_source', source);
    trackEvent('mio_begin_checkout');
  };

  window.mioClarity = {
    event: trackEvent,
    eventOnce: trackPageEventOnce,
    set: setTag,
    trackAddToCart,
    trackMillAddError,
    trackBeginCheckout,
    trackCartRecommendation,
  };

  setTag('mio_event_version', '7');
  setTag('mio_page_type', body.dataset.mioPageType);
  setTag('mio_product_handle', body.dataset.mioProductHandle);
  setTag('mio_collection_handle', body.dataset.mioCollectionHandle);
  setTag('mio_search_state', body.dataset.mioSearchState);

  if (body.dataset.mioPageType === 'product') trackPageEventOnce('mio_product_viewed');
  if (body.dataset.mioPageType === 'collection') trackPageEventOnce('mio_collection_viewed');
  if (body.dataset.mioPageType === 'cart') {
    setTag('mio_cart_surface', 'page');
    setTag('mio_cart_state', document.querySelector('cart-items.is-empty') ? 'empty' : 'populated');
    trackPageEventOnce('mio_cart_viewed');
  }
  if (body.dataset.mioPageType === 'search' && body.dataset.mioSearchState) {
    trackPageEventOnce('mio_search_results_viewed');
  }

  document.addEventListener('mio:cart-drawer-opened', (event) => {
    setTag('mio_cart_surface', 'drawer');
    setTag('mio_cart_state', event.detail?.empty ? 'empty' : 'populated');
    trackEvent('mio_cart_viewed');
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (data = {}) => {
      const addSources = ['product-form', 'quick-add', 'quick-order-list'];
      if (addSources.includes(data.source)) {
        trackAddToCart(data.source, data.millMode, data.cartData?.handle || data.cartData?.items?.[0]?.handle);
      }
    });
  }

  document.querySelectorAll('cart-drawer, #main-cart-footer').forEach((recommendationRoot) => {
    if (typeof IntersectionObserver !== 'function') return;
    const viewedRecommendations = new Set();
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5 || document.hidden) continue;
        if (recommendationRoot.matches('cart-drawer') && !recommendationRoot.classList.contains('active')) continue;
        const recommendation = entry.target.dataset;
        if (!viewedRecommendations.has(recommendation.handle)) {
          viewedRecommendations.add(recommendation.handle);
          trackCartRecommendation('viewed', recommendation);
        }
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.5 });
    const observeRecommendations = () => {
      observer.disconnect();
      recommendationRoot.querySelectorAll('[data-mio-recommendation]').forEach((card) => {
        if (!viewedRecommendations.has(card.dataset.handle)) observer.observe(card);
      });
    };
    observeRecommendations();
    new MutationObserver(observeRecommendations).observe(recommendationRoot, { childList: true, subtree: true });
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) observeRecommendations();
    });
  });

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;
      const recommendation = event.target.closest('a')?.closest('[data-mio-recommendation]');
      if (recommendation) {
        const { handle, category, kind } = recommendation.dataset;
        trackCartRecommendation('clicked', recommendation.dataset);
        try {
          const origins = recommendationOrigins().filter((item) => item.handle !== handle);
          origins.push({ handle, category, kind, at: Date.now() });
          sessionStorage.setItem(recommendationStorageKey, JSON.stringify(origins.slice(-20)));
        } catch {
          return;
        }
      }
      if (event.target.closest('[data-mio-mill-pair-link]')) trackPageEventOnce('mio_collection_pair_selected');
      const checkoutControl = event.target.closest(
        '[data-mio-e2e="cart-checkout-cta"], [data-mio-e2e="cart-drawer-checkout-cta"], a[href^="/checkout"]'
      );
      if (!checkoutControl || checkoutControl.matches(':disabled, [aria-disabled="true"]')) return;
      trackBeginCheckout(checkoutControl.dataset.mioE2e || 'checkout-link');
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const submitter = event.submitter;
      if (submitter?.name === 'checkout') {
        trackBeginCheckout(submitter.dataset.mioE2e || 'checkout-submit');
      }
    },
    true
  );

  window.addEventListener('load', flush, { once: true });
})();
